import { useRef, useState, type MouseEvent, type RefObject } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { TechnicalLabel } from "@/components/TechnicalLabel";

export type LegalDoc = "mentions" | "confidentialite";

const sectionClass = "space-y-2 border-t border-si-line pt-5";
const headingClass = "font-display text-base font-bold text-si-cold";

export function useLegalDialog() {
    const [doc, setDoc] = useState<LegalDoc | null>(null);
    const triggerRef = useRef<HTMLElement | null>(null);
    const open = (next: LegalDoc, event: MouseEvent<HTMLButtonElement>) => {
        triggerRef.current = event.currentTarget;
        setDoc(next);
    };
    return { doc, open, close: () => setDoc(null), triggerRef };
}

export function LegalDialog({ doc, onClose, returnFocusTo }: {
    doc: LegalDoc | null;
    onClose: () => void;
    returnFocusTo: RefObject<HTMLElement | null>;
}) {
    return (
        <Dialog.Root open={!!doc} onOpenChange={(open) => !open && onClose()}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
                <Dialog.Content
                    className="fixed left-1/2 top-1/2 z-50 max-h-[85dvh] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-x-hidden overflow-y-auto break-words border border-si-line bg-si-panel p-6 text-si-cold shadow-panel outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:p-8"
                    onCloseAutoFocus={(event) => {
                        event.preventDefault();
                        returnFocusTo.current?.focus();
                    }}
                    data-testid="legal-dialog"
                >
                    <TechnicalLabel tone="accent">INFORMATIONS JURIDIQUES</TechnicalLabel>
                    <Dialog.Title className="mt-2 pr-2 font-display text-lg sm:pr-8 sm:text-2xl font-extrabold uppercase tracking-tight">
                        {doc === "mentions" ? "MENTIONS LÉGALES" : "POLITIQUE DE CONFIDENTIALITÉ"}
                    </Dialog.Title>
                    <Dialog.Description className="sr-only">
                        {doc === "mentions" ? "Informations sur l’éditeur et l’hébergement du site SI2T." : "Informations sur le traitement des données transmises à SI2T."}
                    </Dialog.Description>

                    {doc === "mentions" && (
                        <div className="mt-6 space-y-6 text-sm leading-relaxed text-si-steel">
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Éditeur du site</h2>
                                <p>SI2T – Solutions d’Ingénierie Industrielle et Technique est exploité par Maxime Saurel, entrepreneur individuel (EI), exerçant sous le nom commercial MVZ PRODUCTION &amp; CO.</p>
                                <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr]">
                                    <dt>SIREN</dt><dd>929 368 728</dd>
                                    <dt>SIRET</dt><dd>929 368 728 00010</dd>
                                    <dt>Adresse</dt><dd>3 BD MONTMARTEL, 26100 ROMANS-SUR-ISÈRE, France</dd>
                                    <dt>Email</dt><dd><a className="underline underline-offset-2" href="mailto:si2tsolutions@outlook.fr">si2tsolutions@outlook.fr</a></dd>
                                    <dt>Téléphone</dt><dd><a className="underline underline-offset-2" href="tel:+33618869474">06 18 86 94 74</a></dd>
                                    <dt>Site</dt><dd>si2tsolutions.fr</dd>
                                </dl>
                                <p><strong className="text-si-cold">Directeur de la publication :</strong> Maxime Saurel</p>
                            </section>
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Hébergement</h2>
                                <p>Scaleway SAS — 8, rue de la Ville-l’Évêque, 75008 Paris, France. Site : <a className="underline underline-offset-2" href="https://www.scaleway.com/">www.scaleway.com</a>.</p>
                                <p>Instance SI2T située en zone FR-PAR-2.</p>
                            </section>
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Propriété intellectuelle</h2>
                                <p>Sauf mention contraire, les textes, éléments graphiques, identité visuelle, documents, illustrations techniques et projets de démonstration présents sur ce site sont protégés. Leur reproduction ou utilisation nécessite une autorisation préalable. Les droits sur les contenus de tiers restent la propriété de leurs titulaires respectifs.</p>
                            </section>
                        </div>
                    )}

                    {doc === "confidentialite" && (
                        <div className="mt-6 space-y-6 text-sm leading-relaxed text-si-steel">
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Responsable du traitement</h2>
                                <p>Maxime Saurel, entrepreneur individuel exploitant SI2T – Solutions d’Ingénierie Industrielle et Technique.</p>
                                <p>3 BD MONTMARTEL, 26100 ROMANS-SUR-ISÈRE, France<br /><a className="underline underline-offset-2" href="mailto:si2tsolutions@outlook.fr">si2tsolutions@outlook.fr</a><br /><a className="underline underline-offset-2" href="tel:+33618869474">06 18 86 94 74</a></p>
                            </section>
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Données collectées</h2>
                                <p>Identité et coordonnées professionnelles transmises volontairement, entreprise, contenu de la demande, informations relatives au projet et fichiers techniques éventuellement transmis via le formulaire.</p>
                            </section>
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Finalités</h2>
                                <p>Les données sont utilisées uniquement pour répondre aux demandes, qualifier le besoin technique, préparer un devis et assurer le suivi commercial associé.</p>
                            </section>
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Bases juridiques</h2>
                                <p>Le traitement initial d’une demande et la préparation d’un devis reposent sur les mesures précontractuelles prises à la demande de la personne concernée. Le suivi commercial raisonnable repose sur l’intérêt légitime de SI2T.</p>
                            </section>
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Destinataires</h2>
                                <p>Les données sont accessibles à SI2T et aux prestataires techniques strictement nécessaires au fonctionnement, à l’hébergement et au stockage du service.</p>
                            </section>
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Durées de conservation</h2>
                                <ul className="list-disc space-y-2 pl-5">
                                    <li>Prospect : 3 ans à compter de la collecte ou du dernier contact émanant du prospect.</li>
                                    <li>Fichiers techniques lorsqu’aucun projet n’est conclu : 90 jours.</li>
                                    <li>Si une relation commerciale est conclue : les informations nécessaires rejoignent le dossier client/projet et sont conservées pendant les durées nécessaires à l’exécution de la relation et aux obligations légales applicables.</li>
                                </ul>
                            </section>
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Vos droits</h2>
                                <p>Vous disposez d’un droit d’accès, de rectification et d’effacement, ainsi que d’un droit à la limitation et d’un droit d’opposition lorsque ces droits sont applicables. Pour exercer vos droits, écrivez à <a className="underline underline-offset-2" href="mailto:si2tsolutions@outlook.fr">si2tsolutions@outlook.fr</a>. Vous pouvez également introduire une réclamation auprès de la CNIL.</p>
                            </section>
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Transferts hors EEE</h2>
                                <p>Les éventuels transferts de données en dehors de l’Espace économique européen dépendent des prestataires techniques retenus et seront documentés avant la mise en ligne du site.</p>
                            </section>
                            <section className={sectionClass}>
                                <h2 className={headingClass}>Cookies et traceurs</h2>
                                <p>Dans l’état technique actuel du site, aucun traceur publicitaire ou analytique non essentiel n’a été identifié. Cette situation pourra évoluer si de nouveaux services sont ajoutés.</p>
                            </section>
                        </div>
                    )}
                    <Dialog.Close
                        className="absolute right-4 top-4 p-1.5 text-si-steel transition-colors duration-fast hover:text-si-cold"
                        aria-label="Fermer"
                        data-testid="legal-dialog-close"
                    >
                        <X size={16} />
                    </Dialog.Close>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
